import { useEffect, useState } from 'react';
import { subscribeToasts, type Toast } from '../lib/toast';

/** How long a message stays up. Long enough to read at a glance mid-shift. */
const LINGER_MS = 3600;

/**
 * Renders whatever `toast()` most recently said.
 *
 * One at a time on purpose: a stack of notices on a 390px screen covers the
 * thing the person was looking at, and these are all short.
 *
 * Sits above the composer rather than at the top of the screen, because the
 * actions that produce them — reacting, pinning, deleting, muting — are all
 * done with a thumb at the bottom of the phone, and that is where the eye
 * already is.
 */
export default function Toaster() {
  const [current, setCurrent] = useState<Toast | null>(null);

  useEffect(() => subscribeToasts(setCurrent), []);

  useEffect(() => {
    if (!current) return;
    const timer = window.setTimeout(() => setCurrent(null), LINGER_MS);
    return () => clearTimeout(timer);
  }, [current]);

  if (!current) return null;

  return (
    <div
      className="pb-safe pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center px-4 pb-4"
      // Announced without stealing focus from whatever the person is doing.
      role="status"
      aria-live="polite"
    >
      <div
        // Keyed so a second message restarts the animation rather than
        // silently swapping the text of one already sitting there.
        key={current.id}
        className={`animate-in-up pointer-events-auto max-w-full rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-lg ${
          current.tone === 'error' ? 'bg-red-600' : 'bg-zinc-800'
        }`}
      >
        {current.message}
      </div>
    </div>
  );
}
